import { groupBy } from 'lodash/collection';
import { uniqBy } from 'lodash/array';
import { get } from 'lodash/object';
import { saveAndLock, unlock, revertAndUnlock } from '@paysera/redux-state-history';
import {
    selectSelection,
    compileObject,
    reindexChildren,
    selectTicketTypeByHash,
} from '%paysera_seat_selection/js/seatSelection/redux';
import { openSnackbar, VARIANT_ERROR } from '%app/js/common/components/material/Snackbar';
import Translator from '%app/js/common/services/translator';
import ObjectDeleteError from '%paysera_seat_selection/js/seatSelection/redux/component/orm/errors/ObjectDeleteError';
import SeatEnumerationError from '%paysera_seat_selection/js/seatSelection/redux/component/orm/errors/SeatEnumerationError';
import getSeatingMapObjectParent from '%paysera_seat_selection/js/seatSelection/services/getSeatingMapObjectParent';
import {
    OBJECT_TYPE_GENERAL_ADMISSION,
    OBJECT_TYPE_SEAT,
} from '%paysera_seat_selection/js/seatSelection/config/constants';
import deleteObject from './deleteObject';
import setSeatingsCount from './setSeatingsCount';
import { extractSeatingsFromSelection } from '%paysera_seat_selection/js/seatSelection/redux/component/orm/services/extractSeatsFromSelection';
import calculateTicketTypesCacheKey from '%paysera_simple_event/js/seatSelectionIntegration/redux/actions/calculateTicketTypeCacheKey';
import calculateReservationCapacity from '%paysera_seat_selection/js/seatSelection/redux/component/orm/actions/calculateReservationCapacity';
import setEventTicketsCount from '%paysera_simple_event/js/seatSelectionIntegration/redux/actions/setEventTicketsCount';

function deleteSelectedObjects() {
    return (dispatch, getState) => {
        const { objects } = selectSelection(getState());
        if (objects.length === 0) {
            return;
        }

        objects.forEach((object) => {
            dispatch(saveAndLock());
            const parent = getSeatingMapObjectParent(object);
            try {
                const groupedSeatings = groupBy(
                    extractSeatingsFromSelection([object]).filter(seating => seating.ticketType !== null),
                    seating => seating.ticketType.hash,
                );
                Object.entries(groupedSeatings).forEach(([hash, group]) => {
                    const seatingsToDelete = group.reduce((acc, seating) => {
                        if (seating.objectType === OBJECT_TYPE_SEAT && seating.enabled === true) {
                            return acc + 1;
                        }
                        if (seating.objectType === OBJECT_TYPE_GENERAL_ADMISSION) {
                            return acc + seating.seatsTotal;
                        }

                        return acc;
                    }, 0);

                    const newSeatingCount = selectTicketTypeByHash(getState(), hash).seatingsCount - seatingsToDelete;
                    dispatch(setSeatingsCount({
                        hash,
                        count: newSeatingCount,
                    }));
                    dispatch(setEventTicketsCount({ hash, count: newSeatingCount }));
                });
                dispatch(deleteObject({ type: object.objectType, id: object.id }));
                dispatch(reindexChildren({ type: parent.objectType, id: parent.id }));
                dispatch(compileObject(object));
                dispatch(calculateTicketTypesCacheKey());

                const reservations = extractSeatingsFromSelection([object])
                    .filter(seating => get(seating, 'reservation', null) !== null)
                    .map(seating => seating.reservation);

                uniqBy(reservations, 'id').forEach(
                    reservation => dispatch(calculateReservationCapacity(reservation)),
                );
                dispatch(unlock());
            } catch (error) {
                if (error instanceof ObjectDeleteError) {
                    dispatch(revertAndUnlock());
                    dispatch(openSnackbar({
                        variant: VARIANT_ERROR,
                        secondaryMessages: error.getMessages(),
                        message: Translator.trans('seat_selection.delete_error.selected_objects'),
                    }));
                    return;
                }
                if (error instanceof SeatEnumerationError) {
                    dispatch(revertAndUnlock());
                    dispatch(openSnackbar({
                        variant: VARIANT_ERROR,
                        message: error.message,
                    }));
                    return;
                }
                dispatch(unlock());
                throw error;
            }
        });
    };
}

export default deleteSelectedObjects;
